import React, { Fragment, useEffect, useState } from 'react';

import '../../styles/containers/Activities.css';
import { History, LocationState } from 'history';
import { AxiosError } from 'axios';
import uniqueId from 'lodash/uniqueId';

import * as Service from '../../services/ActivitiesService';
import * as GuideService from '../../services/CityGuideService';
import Modal from '../../components/utils/ModalComponet';
import Spinner from '../../components/utils/Spinner';
import {
  getCityLocation,
  getTimeZoneData,
  getBase64,
  openLink,
} from '../../infrastructure/utils/common';
import CityList from '../../components/activities/Cities/Cities';
import { City, CityGuide } from '../../types/City';
import Error from '../../components/stateMessage/Error';

import { mappedCities } from './helpers';

type State = {
  citiesLoaded: boolean;
  deactive: boolean;
  openModal: boolean;
  deletedCity: string;
  errorText: string | null;
  isError: boolean;
  cities: City[];
  guides: CityGuide[];
};

type Props = {
  history: History<LocationState>;
};

const defaultState: State = {
  citiesLoaded: true,
  deactive: false,
  openModal: false,
  deletedCity: '',
  isError: false,
  cities: [],
  guides: [],
  errorText: null,
};

const Cities = ({ history }: Props) => {
  const [state, setState] = useState<State>(defaultState);
  const onSetStateHandle = (state: Partial<State>) => {
    setState(prev => ({ ...prev, ...state }));
  };

  const initCities = async () => {
    onSetStateHandle({ citiesLoaded: true });
    try {
      const [cities, guides] = await Promise.all([
        Service.getCities(),
        GuideService.getGuides(),
      ]);
      onSetStateHandle({
        cities: mappedCities(cities),
        guides,
        citiesLoaded: false,
      });
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
      setTimeout(hideMessage, 5 * 1000);
    } finally {
      onSetStateHandle({ citiesLoaded: false });
    }
  };

  const hideMessage = () => {
    onSetStateHandle({ isError: false });
  };

  const changeOrder = async (cities: City[], city: City) => {
    const sortedCities = cities.sort((a, b) =>
      a.order && b.order ? a.order - b.order : 0
    );

    onSetStateHandle({ cities: sortedCities });
    try {
      await Service.changeCityStatus({
        ...city,
        picture: city.picture.data || '',
      });
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
      setTimeout(hideMessage, 5 * 1000);
    } finally {
      initCities();
    }
  };

  const changeCityStatus = async city => {
    const { cities } = state;
    const idx = cities.findIndex(el => el.name === city);
    if (idx === -1) {
      return;
    }
    const currentCity = cities[idx];
    let { location, tz, timeOffset } = currentCity;
    const {
      status,
      picture: { data },
    } = currentCity;
    const newStatus = status === 'active' ? 'inactive' : 'active';
    try {
      if (!location) {
        const results = (await getCityLocation(city)) as { lat(); lng() };
        location = {
          lat: results.lat(),
          lng: results.lng(),
        };
        const tzdata = await getTimeZoneData(location.lat, location.lng);
        tz = tzdata.data.timeZoneId;
        timeOffset = (tzdata.data.rawOffset / 3600) * 60;
      }
      await Service.changeCityStatus({
        ...currentCity,
        status: newStatus,
        picture: data,
        location,
        tz,
        timeOffset,
      });
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
      setTimeout(hideMessage, 5 * 1000);
    } finally {
      initCities();
    }
  };

  const changeTopStatus = async city => {
    const { cities } = state;
    const idx = cities.findIndex(el => el.name === city);
    if (idx === -1) {
      return;
    }
    const currentCity = cities[idx];
    const {
      top,
      picture: { data },
    } = currentCity;
    const newTop = !top;

    const updatedCity = {
      ...currentCity,
      picture: data,
      top: newTop,
    };
    try {
      await Service.changeCityStatus(updatedCity);
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
      setTimeout(hideMessage, 5 * 1000);
    } finally {
      initCities();
    }
  };

  const toDelete = (name: string) => {
    onSetStateHandle({ openModal: true, deletedCity: name });
  };

  const closeModal = () => {
    onSetStateHandle({ openModal: false });
  };

  const deleteCity = async () => {
    const { deletedCity } = state;
    try {
      await Service.deleteCity(deletedCity);
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        openModal: false,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
      setTimeout(hideMessage, 5 * 1000);
    } finally {
      onSetStateHandle({ openModal: false });
      initCities();
    }
  };

  const uploadPhoto = async (target, name) => {
    const { cities } = state;
    if (target.files.length === 0) {
      return;
    }
    if (
      target.files[0].type !== 'image/png' &&
      target.files[0].type !== 'image/jpeg'
    ) {
      onSetStateHandle({
        isError: true,
        errorText: 'You can upload only png or jpg files',
      });
      setTimeout(hideMessage, 5 * 1000);
      return;
    }
    const idx = cities.findIndex(el => el.name === name);
    if (idx === -1) {
      return;
    }
    const currentCity = cities[idx];
    currentCity.picture = {
      isNew: true,
      blob: target.files[0],
      data: (await getBase64(target.files[0])) as string,
    };
    try {
      const uId = uniqueId(name + new Date());
      const url = await Service.uploadCityImage(target.files[0], uId);
      await Service.changeCityStatus({
        ...currentCity,
        picture: url,
      });
      initCities();
    } catch (e) {
      const error = e as AxiosError;
      onSetStateHandle({
        isError: true,
        errorText: `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`,
      });
    } finally {
      setTimeout(hideMessage, 5 * 1000);
    }
  };

  const handleOnExploreClick = (event, name) => {
    const url = `cities/explore/${name}`;
    openLink(event, url, history);
  };

  const handleOnGuideClick = (event, name) => {
    const url = `cities/guide/${name}`;
    openLink(event, url, history);
  };

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    initCities();
  }, []);

  useEffect(() => {
    return () => {
      document.documentElement.style.overflow = 'auto';
      onSetStateHandle({ deactive: true });
    };
  }, []);

  const {
    citiesLoaded,
    cities,
    openModal,
    deletedCity,
    guides,
    isError,
    errorText,
  } = state;

  if (citiesLoaded) {
    return <Spinner />;
  }

  return (
    <Fragment>
      {!!errorText && <Error text={errorText} />}
      <Modal
        open={openModal}
        closeModal={closeModal}
        cancel={closeModal}
        action={deleteCity}
        actionButtonText='Delete'
      >
        <div style={{ marginBottom: '40px' }}>
          Are you sure you want to delete <b>{deletedCity}</b>? If this city
          have some products in shopify, it&apos;s will be restored
        </div>
      </Modal>
      <CityList
        isError={isError}
        guides={guides}
        changeOrder={changeOrder}
        handleOnExploreClick={handleOnExploreClick}
        handleOnGuideClick={handleOnGuideClick}
        handleOnTopClick={changeTopStatus}
        uploadPhoto={uploadPhoto}
        deleteCity={toDelete}
        handleOnStatusClick={changeCityStatus}
        cities={cities}
      />
    </Fragment>
  );
};
export default Cities;
